import * as React from "react"

import { cva, type VariantProps } from "./cva.ts"
import { invokeRef } from "./extend-ref.ts"

export const TextControl = React.forwardRef(
  ({ className, invalid = false, type = "text", ...props }, ref) => {
    const Component = type === "textarea" ? "textarea" : ("input" as React.ElementType)

    const handleRef = React.useCallback(
      (current: HTMLInputElement | HTMLTextAreaElement | null) => {
        invokeRef(ref, current)

        if (current) {
          current.removeEventListener("input", handleInputResize)
          current.addEventListener("input", handleInputResize)
        }
      },
      [ref],
    )

    return (
      <Component
        className={classForTextControl({ className, invalid })}
        type={type === "textarea" ? undefined : type}
        ref={handleRef}
        {...props}
      />
    )
  },
) as PolymorphicTextControl<VariantProps<typeof classForTextControl>>

TextControl.displayName = "TextControl"

export const classForTextControl = cva(
  [
    "flex w-stretch px-3 py-2 h-9 outline-none",
    "bg-background text-sm text-gray-12",
    "bshadow rounded-md transition-all",
    "placeholder:text-gray-11",
    "autofill:bg-transparent",
    "disabled:cursor-not-allowed disabled:placeholder:text-gray-8",
    "focus-visible:ring-2",
    "resize-none [&:is(textarea)]:min-h-[3.75rem]",
  ],
  {
    variants: {
      invalid: {
        true: "bshadow-tomato-11 focus-visible:bshadow-tomato-11 focus-visible:ring-tomato-5",
        false: "bshadow-gray-6 focus-visible:bshadow-gray-8 focus-visible:ring-gray-5",
      },
    },
  },
)

const handleInputResize = ({ target }: any) => {
  target.style.height = "auto"
  target.style.height = target.scrollHeight + "px"
}

export type PolymorphicTextControl<Props> = {
  <C extends TextSolusLineTypes | TextMultiLineTypes>(
    props: {
      ref?: React.ForwardedRef<
        C extends TextMultiLineTypes
          ? HTMLElementTagNameMap["textarea"]
          : HTMLElementTagNameMap["input"]
      >
      type?: C
    } & Omit<React.ComponentProps<"textarea">, "ref" | "type"> &
      Props & {
        pattern?: HTMLElementTagNameMap["input"]["pattern"]
      },
  ): React.ReactElement | null

  displayName?: string | undefined
}

type TextSolusLineTypes = "email" | "password" | "tel" | "text" | "url" | undefined
type TextMultiLineTypes = "textarea"
